import { Link } from 'react-router-dom';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { Box } from '@mui/material';

import type { ArAgingPayload, CleanClaimsPayload } from './api';
import { ageLabels, money, outcomes, rate, share } from './format';

import './Revenue.css';

export function CleanClaimsCard({
  data,
  onOpen,
}: {
  data: CleanClaimsPayload;
  onOpen?: () => void;
}) {
  return (
    <section className="rw-compact">
      <Link
        className="rw-card-hit"
        aria-label="Explore clean claims"
        to="/jotbill/analytics?view=clean"
        onClick={onOpen}
      />
      <div className="rw-compact-title">
        <h3>Clean claims</h3>
        <span>Last 90 days</span>
      </div>
      <div className="rw-big">{rate(data.counts.clean_rate_pct)}</div>
      <p>Paid without a full denial</p>
      <span className="rw-footnote">
        {data.counts.clean ?? 0} of {data.counts.responded ?? 0} responded claims · excludes pending
      </span>
      <div className="rw-outcome-track" aria-label="Claim outcome distribution">
        {outcomes.map((o, i) => (
          <Box key={o.key} className={`tone-${i}`} sx={{ flex: data.counts[o.key] ?? 0 }} />
        ))}
      </div>
      <div className="rw-compact-caption">
        <span>
          <strong>{data.counts.denied ?? 0}</strong> full denials
        </span>
        <span>
          <strong>{data.counts.pending ?? 0}</strong> awaiting response
        </span>
      </div>
      <div className="rw-compact-footer">
        <span>Understand payer outcomes</span>
        <span className="rw-card-cta">
          Explore clean claims <ArrowForwardRounded />
        </span>
      </div>
    </section>
  );
}

export function ArCard({ data, onOpen }: { data: ArAgingPayload; onOpen?: () => void }) {
  const buckets = ageLabels.map((label, bucket) => ({
    label,
    bucket,
    balance_cents:
      (data.buckets?.find(b => b.bucket === bucket)?.balance_cents ?? 0) +
      (data.patients.buckets?.find(p => p.bucket === bucket)?.balance_cents ?? 0),
  }));
  const balance = (data.balance_cents ?? 0) + (data.patients.all_balance_cents ?? 0);
  const over90 = (data.over_90_cents ?? 0) + (data.patients.over_90_cents ?? 0);
  const maximum = Math.max(...buckets.map(b => b.balance_cents), 1);
  return (
    <section className="rw-compact">
      <Link
        className="rw-card-hit"
        aria-label="Explore A/R"
        to="/jotbill/analytics?view=ar"
        onClick={onOpen}
      />
      <div className="rw-compact-title">
        <h3>Accounts receivable</h3>
        <span>Current snapshot</span>
      </div>
      <div className="rw-big">{money(balance)}</div>
      <p>Insurance and patient balances</p>
      <span className="rw-footnote">
        {money(data.balance_cents)} insurance · {money(data.patients.all_balance_cents)} patient
      </span>
      <div className="rw-mini-aging" aria-label="Outstanding balance by age">
        {buckets.map(b => (
          <div key={b.bucket} aria-label={`${b.label} days: ${money(b.balance_cents)}`}>
            <Box component="i" sx={{ height: `${((b.balance_cents ?? 0) / maximum) * 46}px` }} />
            <span>{b.label}</span>
          </div>
        ))}
      </div>
      <div className="rw-compact-caption">
        <span>
          <strong>{money(over90)}</strong> older than 90 days
        </span>
        <span>{share(over90, balance)} of open balance</span>
      </div>
      <div className="rw-compact-footer">
        <span>Find the next follow-up</span>
        <span className="rw-card-cta">
          Explore A/R <ArrowForwardRounded />
        </span>
      </div>
    </section>
  );
}
